/**
 * Report Apps — TypeScript types for the report-app framework (the 5th TMS app
 * type, after Dashboard / TSM / REF / Filter).
 *
 * The report **filter pane** reuses the filter-app descriptor wire shape
 * (panes / fields / toolbar / profile) projected server-side by
 * {@code FilterDescriptorAssembler}. The one difference: reports have NO
 * {@code mode} block — it is replaced by a {@code format} block
 * ({@code {current, available:[screen, excel]}}) emitted by
 * {@code FilterDescriptorAssembler.projectFormat}. See
 * {@code documentation/implementation/system/tms-report-app.md} (server RP-1).
 *
 * The report **result** ({@code ?action=runReport}) is a generic
 * {@code {header, columns, rows}} contract projected by
 * {@code WebReportData.getReportResultJson} — rendered by the generic
 * {@link ReportTableRenderer}.
 *
 * Jackson Rule #1: every variable typed. Rule #7: module-level constants in
 * SCREAMING_SNAKE_CASE.
 */

// ── Re-exports of shared filter-app descriptor types ─────────────────────────
// Reports reuse the filter-app field / pane / toolbar / profile descriptors
// verbatim (one import place, REF/filter reuse pattern).

export type {
	ButtonDescriptor,
	FieldDescriptor,
	LookupDescriptor,
	OperatorDescriptor,
	OperatorOption,
	PaneDescriptor,
	ProfileDescriptor,
	ProfileOption,
	StatusDescriptor,
	ToolbarDescriptor,
	ToolbarGroup,
	ToolbarGroupKey,
	ActionDescriptor,
} from '../../filter-app/models/filter-app.model';

// ── Module constants (Jackson Rule #7) ───────────────────────────────────────

export const REPORT_ROUTE_PATH: string = 'report/:appName';
export const REPORT_FORMAT_SCREEN: string = 'screen';
export const REPORT_FORMAT_EXCEL: string = 'excel';

// ── Report-specific toolbar action kinds (server RP-4) ───────────────────────
// The server emits {@code kind: 'sql' | 'excel' | 'pdf'} for the report
// toolbar buttons (results_sql_button / results_excel_button /
// results_pdf_button). These are NOT in the filter-app {@code ActionDescriptor}
// union — the report page resolves them directly (it does not route report
// toolbar clicks through the shared {@code TmsToolbarActionDispatcher}, whose
// exhaustive switch would otherwise need report kinds added non-additively).

export const REPORT_ACTION_SQL: string = 'sql';
export const REPORT_ACTION_EXCEL: string = 'excel';
export const REPORT_ACTION_PDF: string = 'pdf';
// Filter-context toolbar kinds the report reuses (filter_search_button →
// 'search', filter_clear_button → 'clear-form'); handled by the report page —
// search runs the report (same path as the magnifier), clear-form resets the
// filter form to its profile defaults.
export const REPORT_ACTION_SEARCH: string = 'search';
export const REPORT_ACTION_CLEAR_FORM: string = 'clear-form';

/** The download/sql action kinds a report toolbar button carries. */
export type ReportActionKind = 'sql' | 'excel' | 'pdf';

// ── Format descriptor (replaces filter/TSM's `mode` block) ───────────────────

export interface FormatOption {
	readonly id: string;      // 'screen' | 'excel'
	readonly label: string;   // 'Screen' | 'Excel'
}

export interface FormatDescriptor {
	readonly current: string;
	readonly available: ReadonlyArray<FormatOption>;
}

// ── Report app descriptor ────────────────────────────────────────────────────
// Mirrors the filter-app {@code AppDescriptor} but with a {@code format} block
// instead of {@code mode}. Imported field/pane/toolbar/profile types come from
// the filter-app model (re-exported above).

import {
	FieldDescriptor,
	PaneDescriptor,
	ProfileDescriptor,
	StatusDescriptor,
	ToolbarDescriptor,
} from '../../filter-app/models/filter-app.model';

export interface ReportAppDescriptor {
	readonly appName: string;
	readonly title: string;
	readonly format: FormatDescriptor;
	readonly formId: string;
	// Intentionally inert: the shared FilterDescriptorAssembler emits `panes` for
	// reports too (the report has a filter pane), but the report page renders a
	// single Results surface (no pane tabs) and the panel reads `fields`, not
	// `panes`. Kept for wire-shape parity with the filter-app descriptor.
	readonly panes: ReadonlyArray<PaneDescriptor>;
	readonly toolbar: ToolbarDescriptor;
	readonly profile: ProfileDescriptor;
	readonly fields: Readonly<Record<string, FieldDescriptor>>;
	readonly status: StatusDescriptor;
}

// ── Report-result contract (runReport JSON) ──────────────────────────────────
// Matches WebReportData.getReportResultJson (server RP-2). The renderer
// consumes {header, columns, rows}; `format` drives the Search download-vs-render
// branch; `referenceId` is the audit handle.

export interface ReportHeaderMetadata {
	readonly label: string;
	readonly value: string;
}

export interface ReportHeader {
	readonly logoDataUri?: string | null;
	readonly title?: string | null;
	readonly asOf?: string | null;
	readonly metadata: ReadonlyArray<ReportHeaderMetadata>;
}

/** One column's projected metadata (item / label / mask / cssRole / width / align). */
export interface ReportColumn {
	readonly item: string;
	readonly label: string;
	readonly mask?: string | null;
	// HTML class name ported from _libv__report.css (e.g. 'detailData',
	// 'reportSummary', 'overlineText', 'fieldName', 'header', 'detailTitle').
	readonly cssRole?: string | null;
	readonly widthPct?: number | null;
	readonly align?: 'l' | 'r' | 'c' | null;
	readonly productUrl?: string | null;
}

/** Typed cell value discriminator (drives sort / negative test, not render). */
export type ReportCellType = 'amount' | 'long' | 'date' | 'string';

export interface ReportCell {
	// Typed value — used for the negative (RED) test + future sort.
	readonly value: string | number | null;
	// Server-pre-masked display string (FscMask owned server-side). The
	// renderer shows THIS; it does not re-implement masks.
	readonly display: string;
	readonly type?: ReportCellType;
	readonly mask?: string | null;
	readonly cssRole?: string | null;
	// Precomputed server-side (ColumnTag RED rule). Renderer just styles it.
	readonly negative?: boolean;
	readonly align?: 'l' | 'r' | 'c' | null;
	readonly productUrl?: string | null;
}

/** Row break level — server-tagged (detail / per-level section footer / grand total). */
export type ReportRowLevel =
	| 'detail'
	| 'sectionFooter_1'
	| 'sectionFooter_2'
	| 'sectionFooter_3'
	| 'grandTotal';

/**
 * Per-page metadata the server stamps on the FIRST row of each legacy page
 * (gated JSON capture in {@code WebReportData.captureJsonRow}). {@code
 * sectionMetadata} carries THAT page's runtime SectionHeader values (Center /
 * Currency / Funding-Entity / Bank) — they vary page to page, unlike the
 * constant top-level {@link ReportHeader}.
 */
export interface ReportPageMeta {
	readonly pageNumber: number;
	readonly sectionMetadata: ReadonlyArray<ReportHeaderMetadata>;
}

export interface ReportRow {
	readonly level: ReportRowLevel;
	readonly cells: ReadonlyArray<ReportCell>;
	// Server-stamped page boundary (gated capture): true on the first row of
	// each legacy page; absent for every other row and for single-page reports.
	readonly pageBreakBefore?: boolean;
	// Present only when pageBreakBefore is true — this page's number + section
	// values. Consumed by ReportPaginationService to regroup the flat rows.
	readonly pageMeta?: ReportPageMeta;
}

/**
 * One legacy page — the client-side grouping {@code ReportPaginationService}
 * produces by splitting the flat {@code rows[]} at {@code pageBreakBefore}
 * markers. Mirrors a legacy printed page: repeated header chrome + this page's
 * {@code sectionMetadata} + its slice of rows.
 */
export interface ReportPage {
	readonly pageNumber: number;
	readonly sectionMetadata: ReadonlyArray<ReportHeaderMetadata>;
	readonly rows: ReadonlyArray<ReportRow>;
}

export interface ReportResult {
	readonly referenceId: string;
	readonly header: ReportHeader;
	readonly columns: ReadonlyArray<ReportColumn>;
	readonly rows: ReadonlyArray<ReportRow>;
	readonly coverPage?: boolean;
	readonly format?: string;
	readonly formId?: string;
}

// ── Path B snapshot variant ──────────────────────────────────────────────────
// Reports are run-and-render — the result is cached server-side by formId
// (rebind re-attaches the same BAP). The thin client slice is UI-only: which
// format was picked, whether the filter panel was open, the result scroll.

export interface ReportAppSnapshot {
	readonly kind: 'report';
	readonly appName: string;
	readonly formId: string | null;
	readonly profileCd: string | null;
	readonly format: string;
	readonly filterPanelOpen: boolean;
	readonly scrollTop: number;
}

/**
 * Does this profile's output go to a FILE rather than the screen? The one place that decides,
 * because three call sites need the same answer and they must not drift: the bootstrap (whether
 * to auto-run at all — a navigation must not download), {@code RunReport} (whether producing the
 * output means handing over a workbook), and the page body (which empty state to show). Tolerates
 * a descriptor with no format block, answering "screen" — the harmless direction: rendering an
 * unexpected report is recoverable, pushing an unexpected file is not.
 */
export function isWorkbookOutput(descriptor: ReportAppDescriptor | null | undefined): boolean {
	return descriptor?.format?.current === REPORT_FORMAT_EXCEL;
}
